import type { Photo as RecipePhoto } from '@platewell/core';
import { PHOTO_PLACEHOLDER, photoUrl } from '@platewell/query';
import { useEffect, useRef, useState } from 'react';
import { useEscapeToClose } from '../hooks/use-escape-to-close.js';
import { API_BASE_URL } from '../lib/api.js';
import { localPreviewUrl } from '../lib/uploads.js';

export interface PhotoLightboxProps {
  photos: readonly RecipePhoto[];
  initialIndex: number;
  alt: string;
  onClose: () => void;
}

/**
 * Full-width viewer for recipe photos.
 *
 * Navigation stacks the next image on top of the current one and only commits the
 * swap after it has loaded, so the stage never collapses mid-transition.
 */
export function PhotoLightbox({ photos, initialIndex, alt, onClose }: PhotoLightboxProps) {
  const [index, setIndex] = useState(() => clampIndex(initialIndex, photos.length));
  const [visible, setVisible] = useState(
    () => photos[clampIndex(initialIndex, photos.length)] ?? null,
  );
  const [incoming, setIncoming] = useState<RecipePhoto | null>(null);
  const multi = photos.length > 1;
  const target = photos[index] ?? null;

  useEffect(() => {
    setIndex(clampIndex(initialIndex, photos.length));
  }, [initialIndex, photos.length]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEscapeToClose(onClose);

  useEffect(() => {
    if (!multi) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft' || event.key === 'k' || event.key === 'K') {
        event.preventDefault();
        setIndex((current) => (current - 1 + photos.length) % photos.length);
      } else if (event.key === 'ArrowRight' || event.key === 'j' || event.key === 'J') {
        event.preventDefault();
        setIndex((current) => (current + 1) % photos.length);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multi, photos.length]);

  useEffect(() => {
    if (target == null) {
      setVisible(null);
      setIncoming(null);
      return;
    }
    if (target.blobId === visible?.blobId) {
      setIncoming(null);
      return;
    }
    setIncoming(target);
  }, [target, visible?.blobId]);

  // Warm the neighbors so the next arrow press paints immediately.
  useEffect(() => {
    if (!multi || target == null) return;
    for (const offset of [-1, 1]) {
      const neighbor = photos[(index + offset + photos.length) % photos.length];
      if (neighbor == null || neighbor.blobId === target.blobId) continue;
      const warm = new Image();
      warm.src = photoUrl(API_BASE_URL, neighbor.blobId, 1200);
    }
  }, [index, multi, photos, target]);

  if (visible == null) return null;

  return (
    <div className="lightbox-backdrop" onClick={onClose} role="presentation">
      <div
        className="lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={alt}
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="lightbox-close" aria-label="Close" onClick={onClose}>
          ×
        </button>

        {multi && (
          <button
            type="button"
            className="lightbox-nav lightbox-prev"
            aria-label="Previous photo"
            onClick={() => setIndex((current) => (current - 1 + photos.length) % photos.length)}
          >
            ‹
          </button>
        )}

        <div className="lightbox-stage">
          {/* Keys keep the already-decoded <img> mounted when it becomes the visible frame. */}
          <LightboxImage key={visible.blobId} photo={visible} alt={alt} />
          {incoming != null && (
            <LightboxImage
              key={incoming.blobId}
              photo={incoming}
              alt={alt}
              pending
              onReady={() => {
                setVisible(incoming);
                setIncoming((current) =>
                  current?.blobId === incoming.blobId ? null : current,
                );
              }}
            />
          )}
        </div>

        {multi && (
          <button
            type="button"
            className="lightbox-nav lightbox-next"
            aria-label="Next photo"
            onClick={() => setIndex((current) => (current + 1) % photos.length)}
          >
            ›
          </button>
        )}

        {multi && (
          <p className="lightbox-count">
            {index + 1} / {photos.length}
          </p>
        )}
      </div>
    </div>
  );
}

function LightboxImage({
  photo,
  alt,
  pending = false,
  onReady,
}: {
  photo: RecipePhoto;
  alt: string;
  pending?: boolean;
  onReady?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const image = ref.current;
    if (image != null && image.complete && image.naturalWidth > 0) {
      setLoaded(true);
      if (pending) onReadyRef.current?.();
    }
  }, [pending, photo.blobId]);

  const classes = ['lightbox-photo'];
  if (pending) classes.push('lightbox-photo-pending');
  if (!loaded) classes.push('lightbox-photo-loading');

  return (
    <img
      ref={ref}
      className={classes.join(' ')}
      src={photoUrl(API_BASE_URL, photo.blobId, 1200)}
      alt={alt}
      width={photo.width || undefined}
      height={photo.height || undefined}
      decoding="async"
      onLoad={() => {
        setLoaded(true);
        if (pending) onReadyRef.current?.();
      }}
      onError={(event) => {
        const local = localPreviewUrl(photo.blobId);
        event.currentTarget.src = local ?? PHOTO_PLACEHOLDER;
        setLoaded(true);
        if (pending) onReadyRef.current?.();
      }}
    />
  );
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(index, 0), length - 1);
}
